import { Eye, HelpCircle } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import type { FlashcardResponse } from '@/api/types/flashcard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { useFlashcardStore } from '@/store/useFlashcardStore'

const RATING_LABELS = [
  { rating: 1 as const, label: 'Again', color: 'bg-red-500 hover:bg-red-600' },
  { rating: 2 as const, label: 'Hard', color: 'bg-orange-500 hover:bg-orange-600' },
  { rating: 3 as const, label: 'Good', color: 'bg-green-500 hover:bg-green-600' },
  { rating: 4 as const, label: 'Easy', color: 'bg-blue-500 hover:bg-blue-600' },
]

export const ReviewSession = () => {
  const { reviewCards, submitReview, fetchReviewCards, fetchStats } = useFlashcardStore()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const { isSubmitting, run } = useSubmitLock()

  const currentCard: FlashcardResponse | undefined = reviewCards[currentIndex]

  const handleReveal = useCallback(() => {
    setShowAnswer(true)
  }, [])

  const handleRate = useCallback(
    async (rating: 1 | 2 | 3 | 4) => {
      if (!currentCard) return
      await run(async () => {
        try {
          await submitReview(currentCard.id, rating)
          setShowAnswer(false)
          setShowHint(false)

          if (currentIndex >= reviewCards.length - 1) {
            // 全カード完了 → リフレッシュ
            await fetchReviewCards()
            await fetchStats()
            setCurrentIndex(0)
          } else {
            setCurrentIndex((prev) => prev + 1)
          }
        } catch {
          toast.error('レビューの送信に失敗しました')
        }
      })
    },
    [currentCard, currentIndex, reviewCards.length, submitReview, fetchReviewCards, fetchStats, run],
  )

  if (!currentCard) return null

  return (
    <div className="flex flex-col h-full p-4">
      {/* Progress */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground">
          {currentIndex + 1} / {reviewCards.length}
        </span>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-xs">
            {currentCard.question_format}
          </Badge>
          {currentCard.is_leech && <Badge variant="destructive">Leech</Badge>}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1 bg-muted rounded-full mb-6">
        <div
          className="h-full bg-primary rounded-full transition-all"
          style={{ width: `${((currentIndex + 1) / reviewCards.length) * 100}%` }}
        />
      </div>

      {/* Question card */}
      <Card className="p-6 flex-1 flex flex-col">
        {/* Front (Question) */}
        <div className="flex-1">
          <div className="text-xs text-muted-foreground mb-2 uppercase">Question</div>
          <p className="text-lg leading-relaxed">{currentCard.front_text}</p>

          {/* Choices for cloze_choice format */}
          {currentCard.question_format === 'cloze_choice' && currentCard.choices && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              {currentCard.choices.map((choice, i) => (
                <div key={i} className="p-2 rounded border text-sm text-center">
                  {choice}
                </div>
              ))}
            </div>
          )}

          {/* JA hint toggle */}
          {currentCard.ja_hint && (
            <div className="mt-4">
              <button
                onClick={() => setShowHint(!showHint)}
                className="flex items-center gap-1 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <HelpCircle className="w-3 h-3" />
                {showHint ? 'ヒントを隠す' : '日本語ヒント'}
              </button>
              {showHint && (
                <div className="mt-2 p-2 bg-yellow-500/10 rounded text-sm text-yellow-700 dark:text-yellow-300">
                  {currentCard.ja_hint}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Divider & Answer */}
        {showAnswer ? (
          <div className="border-t pt-4 mt-4">
            <div className="text-xs text-muted-foreground mb-2 uppercase">Answer</div>
            <p className="text-lg font-medium text-green-600 dark:text-green-400">{currentCard.back_text}</p>
          </div>
        ) : (
          <div className="mt-6">
            <Button onClick={handleReveal} className="w-full" size="lg">
              <Eye className="w-4 h-4 mr-2" />
              答えを見る
            </Button>
          </div>
        )}
      </Card>

      {/* Rating buttons */}
      {showAnswer && (
        <div className="grid grid-cols-4 gap-2 mt-4">
          {RATING_LABELS.map(({ rating, label, color }) => (
            <Button
              key={rating}
              onClick={() => handleRate(rating)}
              disabled={isSubmitting}
              className={`${color} text-white text-sm py-3`}
            >
              {label}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}
